import React from "react";
import { Box, Typography } from "@mui/material";
import { COLORS } from "../../common/colors";

export const DAY_CELL_WIDTH = 20;
export const DAY_CELL_GAP = 3;
// Left gutter shared by every timeline so day columns line up; wide enough for "12h".
export const AXIS_GUTTER = 36;

// Short enough for a 20px column; two letters where one would be ambiguous (T, S).
const WEEKDAY_LETTERS = ["Su", "M", "T", "W", "Th", "F", "Sa"];

// "Oct 12" overruns its column, so a Monday label this close to the end would hit "Today".
const TODAY_LABEL_COLUMNS = 3;

function parseDay(date: string): Date {
  return new Date(date + "T00:00:00");
}

export function weekdayLetter(date: string): string {
  return WEEKDAY_LETTERS[parseDay(date).getDay()];
}

// Dates under each Monday so users can name the week ("last Tuesday"), and "Today" on the last day.
export function axisDateLabels(dates: string[]): string[] {
  return dates.map((date, idx) => {
    if (idx === dates.length - 1) return "Today";
    if (idx >= dates.length - TODAY_LABEL_COLUMNS) return "";
    const d = parseDay(date);
    if (d.getDay() !== 1) return "";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  });
}

export function formatDay(date: string): string {
  return parseDay(date).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric"
  });
}

export function DayAxis({ dates }: { dates: string[] }): JSX.Element {
  const dateLabels = axisDateLabels(dates);
  const lastIdx = dates.length - 1;
  const rowSx = {
    display: "flex",
    gap: `${DAY_CELL_GAP}px`,
    pl: `${AXIS_GUTTER}px`
  };
  const textSx = {
    width: DAY_CELL_WIDTH,
    flexShrink: 0,
    fontSize: 11,
    lineHeight: 1.3,
    fontFamily: "Work Sans, sans-serif",
    color: COLORS.GREY_900
  };
  return (
    <Box sx={{ mt: "2px" }}>
      <Box sx={rowSx}>
        {dates.map((date, idx) => (
          <Typography
            key={date}
            sx={{
              ...textSx,
              textAlign: "center",
              fontWeight: idx === lastIdx ? 700 : 400
            }}
          >
            {weekdayLetter(date)}
          </Typography>
        ))}
      </Box>
      <Box sx={rowSx}>
        {dates.map((date, idx) => (
          <Box
            key={date}
            sx={{
              width: DAY_CELL_WIDTH,
              flexShrink: 0,
              position: "relative",
              height: 15
            }}
          >
            {dateLabels[idx] && (
              <Typography
                sx={{
                  ...textSx,
                  width: "auto",
                  position: "absolute",
                  whiteSpace: "nowrap",
                  // Today ends at its column; week starts begin at theirs.
                  ...(idx === lastIdx ? { right: 0 } : { left: 0 }),
                  color: COLORS.GREY_800
                }}
              >
                {dateLabels[idx]}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
